import { action, makeObservable, observable, runInAction } from 'mobx';

import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import type { ChartSessionsStore } from '@/stores/local/shared/ChartSessionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { PopupStore } from '@/stores/local/shared/PopupStore';
import { FieldTextStore } from '@/stores/local/shared/form/FieldTextStore';
import { validateMaxLength } from '@/utils/validators';

export type RenameSessionModalEntity = {
  sessionId: string;
  title: string;
};

type RenameSessionModalStoreParams = {
  chartSessionsStore: ChartSessionsStore;
};

export class RenameSessionModalStore extends PopupStore<RenameSessionModalEntity | null> implements ILocalStore {
  readonly chartSessionsStore: ChartSessionsStore;
  readonly fieldText = new FieldTextStore({
    defaultValue: '',
    required: true,
    validator: validateMaxLength(200, 'Не более 200 символов'),
  });
  readonly submitMeta = new MetaStore();

  constructor({ chartSessionsStore }: RenameSessionModalStoreParams) {
    super();
    this.chartSessionsStore = chartSessionsStore;

    makeObservable<RenameSessionModalStore, never>(this, {
      submitMeta: observable.ref,
      submit: action.bound,
    });
  }

  override open(entity: RenameSessionModalEntity | null = null): void {
    super.open(entity);
    this.fieldText.setValue(entity?.title ?? '');
  }

  async submit(): Promise<void> {
    this.fieldText.validate();

    if (this.fieldText.hasError) {
      return;
    }

    const entity = this.entity;
    const title = this.fieldText.value?.trim();

    if (!entity || !title) {
      return;
    }

    this.submitMeta.setLoadedStartMeta();

    const success = await this.chartSessionsStore.updateSessionTitle(entity.sessionId, title);

    runInAction(() => {
      if (success) {
        this.submitMeta.setLoadedSuccessMeta();
        notificationsStore.showSuccess('Название чата изменено');
        this.close();
      } else {
        this.submitMeta.setLoadedErrorMeta();
        notificationsStore.showError('Не удалось изменить название, попробуйте позже');
      }
    });
  }

  destroy(): void {
    this.fieldText.destroy();
    this.submitMeta.destroy();
  }
}
